import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Button,
} from '@mui/material';
import {
  ExpandMore,
  Email,
  Sync,
  CheckCircle,
  Cancel,
  PhoneForwarded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGetActiveSynthflowAccountsQuery, useSyncSynthflowAccountsMutation } from '../store/api/synthflowApi';
import { useGetAgentsForAccountQuery, useSyncSynthflowAgentsMutation } from '../store/api/synthflowAgentsApi';

export const AgentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const {
    data: accounts = [],
    isLoading: accountsLoading,
    error: accountsError
  } = useGetActiveSynthflowAccountsQuery();

  const [syncAccounts, { isLoading: isSyncingAccounts }] = useSyncSynthflowAccountsMutation();
  const [syncAgents, { isLoading: isSyncingAgents }] = useSyncSynthflowAgentsMutation();

  const handleSyncAccounts = async () => {
    try {
      await syncAccounts().unwrap();
      setSyncMessage({ type: 'success', text: 'Accounts synced successfully' });
    } catch (error: any) {
      setSyncMessage({
        type: 'error',
        text: `Failed to sync accounts: ${error?.data?.detail || error?.message || 'Unknown error'}`
      });
    }
  };

  const handleSyncAgents = async () => {
    try {
      const result = await syncAgents().unwrap();
      setSyncMessage({
        type: 'success',
        text: `Agents synced successfully: ${result.total_agents_fetched} fetched, ${result.total_agents_created} created, ${result.total_agents_updated} updated`
      });
    } catch (error: any) {
      setSyncMessage({
        type: 'error',
        text: `Failed to sync agents: ${error?.data?.detail || error?.message || 'Unknown error'}`
      });
    }
  };

  const handleConfigureCallSummary = (agentId: number) => {
    navigate(`/aigent/agents/${agentId}/call-summary`);
  };

  const handleOutboundCalling = (agentId: number, accountId: number) => {
    navigate(`/outbound-calling?agent_id=${agentId}&account_id=${accountId}`);
  };


  if (accountsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (accountsError) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Agents
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Failed to load Synthflow accounts
          </Typography>
          <Typography variant="body2" gutterBottom>
            Error details: {
              (accountsError as any)?.data?.detail ||
              (accountsError as any)?.message ||
              'Unknown error'
            }
          </Typography>
          <Typography variant="body2">
            Please check your Settings page to ensure your Umbrella User ID is configured correctly.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Agents
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            View and configure all agents across your Synthflow accounts
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Sync />}
            onClick={handleSyncAccounts}
            disabled={isSyncingAccounts}
          >
            {isSyncingAccounts ? 'Syncing...' : 'Sync Accounts'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Sync />}
            onClick={handleSyncAgents}
            disabled={isSyncingAgents}
          >
            {isSyncingAgents ? 'Syncing...' : 'Sync Agents'}
          </Button>
        </Box>
      </Box>

      {syncMessage && (
        <Alert
          severity={syncMessage.type}
          onClose={() => setSyncMessage(null)}
          sx={{ mb: 3 }}
        >
          {syncMessage.text}
        </Alert>
      )}

      {accounts.length === 0 ? (
        <Card>
          <CardContent>
            <Alert severity="info">
              <Typography variant="body1">
                No Synthflow accounts found. Please sync your accounts first or contact your administrator.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {accounts.map((account) => (
            <AccountAgentsAccordion
              key={account.id}
              account={account}
              onConfigureCallSummary={handleConfigureCallSummary}
              onOutboundCalling={handleOutboundCalling}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

interface AccountAgentsAccordionProps {
  account: { id: number; name: string };
  onConfigureCallSummary: (agentId: number) => void;
  onOutboundCalling: (agentId: number, accountId: number) => void;
}

const AccountAgentsAccordion: React.FC<AccountAgentsAccordionProps> = ({
  account,
  onConfigureCallSummary,
  onOutboundCalling,
}) => {
  const {
    data: agents = [],
    isLoading: agentsLoading,
    error: agentsError
  } = useGetAgentsForAccountQuery(account.id);

  return (
    <Accordion defaultExpanded sx={{ mb: 2 }}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
        }}
      >
        <Typography variant="h6">
          {account.name}
          <Chip
            label={`${agents.length} agent${agents.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{ ml: 2, backgroundColor: 'primary.light', color: 'primary.contrastText' }}
          />
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {agentsLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : agentsError ? (
          <Alert severity="error">
            Failed to load agents: {
              (agentsError as any)?.data?.detail ||
              (agentsError as any)?.message ||
              'Unknown error'
            }
          </Alert>
        ) : agents.length === 0 ? (
          <Alert severity="info">
            No agents found for this account. Try syncing agents or check your Synthflow account.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Agent Name</strong></TableCell>
                  <TableCell><strong>Phone Number</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Webhook Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agents.map((agent) => (
                  <TableRow key={agent.id} hover>
                    <TableCell>{agent.name}</TableCell>
                    <TableCell>{agent.phone_number || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={agent.type || 'Unknown'}
                        size="small"
                        sx={{
                          backgroundColor: agent.type === 'outbound' ? '#e3f2fd' : 
                                         agent.type === 'inbound' ? '#f3e5f5' : 
                                         agent.type === 'widget' ? '#e8f5e8' : '#f5f5f5',
                          color: agent.type === 'outbound' ? '#1976d2' : 
                                agent.type === 'inbound' ? '#7b1fa2' : 
                                agent.type === 'widget' ? '#388e3c' : '#666666',
                          fontWeight: 'bold'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {agent.webhook_enabled ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Enabled"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          icon={<Cancel />}
                          label="Disabled"
                          size="small"
                          color="default"
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        <Tooltip title="Configure Call Summary Emails">
                          <IconButton
                            color="primary"
                            onClick={() => onConfigureCallSummary(agent.id)}
                          >
                            <Email />
                          </IconButton>
                        </Tooltip>
                        {/* Only show outbound calling icon for outbound agents with phone numbers */}
                        {agent.type === 'outbound' && agent.phone_number && (
                          <Tooltip title="Outbound Calling Campaigns">
                            <IconButton
                              color="secondary"
                              onClick={() => onOutboundCalling(agent.id, account.id)}
                            >
                              <PhoneForwarded />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

